import { useState } from "react";
import { trpc } from "@/lib/trpc";

type ProductId =
  | "sensorik_analyse"
  | "lokal_analyse"
  | "geo_analyse"
  | "seo_analyse"
  | "conversion_analyse"
  | "accessibility_analyse"
  | "datenschutz_analyse"
  | "ecommerce_analyse"
  | "wettbewerb_analyse"
  | "sensorik_mit_videocall"
  | "lokal_mit_videocall"
  | "geo_mit_videocall"
  | "seo_mit_videocall"
  | "conversion_mit_videocall"
  | "accessibility_mit_videocall"
  | "datenschutz_mit_videocall"
  | "ecommerce_mit_videocall"
  | "wettbewerb_mit_videocall"
  | "website_realitaetscheck"
  | "marketing_realitaetscheck"
  | "komplett_paket"
  | "website_realitaetscheck_mit_videocall"
  | "marketing_realitaetscheck_mit_videocall"
  | "komplett_paket_mit_videocall"
  | "google_ads_potenzialanalyse";

interface CheckoutButtonProps {
  productId: ProductId;
  label?: string;
  price?: string;
  accentColor?: string;
  targetUrl?: string;
  returnPath?: string;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

/**
 * Universeller Stripe-Checkout-Button für alle Barometer-Produkte.
 * Leitet den Nutzer direkt zur Stripe-Checkout-Seite weiter.
 */
export function CheckoutButton({
  productId,
  label = "Jetzt kaufen",
  price,
  accentColor = "#6d0000",
  targetUrl,
  returnPath = "/",
  className = "",
  disabled = false,
  fullWidth = false,
}: CheckoutButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckout = trpc.payment.createCheckout.useMutation();

  const handleClick = async () => {
    if (disabled || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await createCheckout.mutateAsync({
        origin: window.location.origin,
        productId,
        targetUrl,
        returnPath,
      });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      setError("Checkout konnte nicht gestartet werden. Bitte erneut versuchen.");
      console.error("[CheckoutButton] Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={fullWidth ? "w-full" : "inline-block"}>
      <button
        onClick={handleClick}
        disabled={disabled || loading}
        className={`font-black uppercase tracking-widest text-sm text-white px-8 py-4 transition-opacity ${fullWidth ? "w-full" : ""} ${className}`}
        style={{
          backgroundColor: disabled ? "#9ca3af" : accentColor,
          fontFamily: "'Bebas Neue', sans-serif",
          letterSpacing: "0.1em",
          opacity: loading ? 0.75 : 1,
          cursor: disabled || loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Weiterleitung …
          </span>
        ) : (
          <span>
            {label}
            {price && (
              <span className="ml-2 opacity-80">· {price}</span>
            )}
          </span>
        )}
      </button>
      {error && (
        <p className="text-red-500 text-xs mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {error}
        </p>
      )}
    </div>
  );
}
